/**
 * L1LockIn — chore-card scale-down + check overlay (Plan 02-15 / UI-SPEC §11).
 *
 * Tier L1 fires on `chore.claimed` for OWN claim only (D-10). The visual
 * effect is a 220ms `scale: 0.96 → 1` on the targeted ChoreCard plus a
 * brief Check icon overlay (220ms).
 *
 * Implementation seam: this component does NOT directly mutate the
 * ChoreCard — that would couple the celebration system to PoolBoardRoute
 * internals. Instead, it dispatches a CustomEvent on the document keyed
 * `stell:l1:<instanceId>` that ChoreCard can listen for. This keeps
 * the celebration system decoupled and lets future components (a future
 * "my chores list" widget on a non-pool route) render the same visual
 * scale effect by listening on the same event name.
 *
 * For v1 the ChoreCard listener has not been wired (Plan 02-09 ChoreCard
 * landed before Plan 02-15) — adding it is a single useEffect addition
 * in a follow-up plan. Until then, the L1 tier renders nothing visible
 * but completes the audio gate (the lock-in.wav still plays via the
 * useCelebration hook), which is the primary motivational feedback at
 * tier 1.
 *
 * Reduced-motion: collapses to a no-op.
 *
 * The side effects live in TWO effects and the halves are NOT symmetric —
 * issue #81 established the shape on L3 and #82 applied it here. The full
 * argument is in L3ApprovedBurst.tsx's header; read it before touching either
 * dep array. In brief:
 *
 *   A (the celebration) dispatches `stell:l1` once per instanceId, guarded by
 *   a ref, and MUST NEVER grow a cleanup — the guard is only safe in an effect
 *   that has none.
 *
 *   B (the lifetime) owns the timer and its clearTimeout and MUST NEVER be
 *   ref-guarded: React runs a cleanup BEFORE the re-run it would then skip, so
 *   a guard here clears the timer and arms no replacement — `onComplete` never
 *   fires and the celebration hangs on screen forever.
 *
 * CelebrationProvider passes a fresh `() => removeEvent(ev.id)` arrow on every
 * render, so `onComplete` is reached through a latest-ref (precedent:
 * useCelebration.ts) rather than listed as a dependency.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { TIMINGS } from './timings';

interface L1LockInProps {
  instanceId: string;
  reducedMotion: boolean;
  onComplete: () => void;
}

export function L1LockIn({
  instanceId,
  reducedMotion,
  onComplete,
}: L1LockInProps): ReactNode {
  // Latest-ref, so effect B never lists the provider's arrow as a dependency.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const firedFor = useRef<string | null>(null);

  // A — the celebration. One `stell:l1` dispatch per instanceId. See the
  // header: this effect MUST NEVER grow a cleanup, which is what makes the
  // ref-guard safe. It reads `reducedMotion` from the mount-time closure
  // deliberately (useCelebration snapshots it onto the event, so it is
  // immutable for an instance's lifetime anyway).
  useEffect(() => {
    if (firedFor.current === instanceId) return;
    firedFor.current = instanceId;
    if (reducedMotion) return;

    // Decouple celebration → card visual via CustomEvent. ChoreCard (or
    // any future consumer) listens for this event and applies its own
    // motion.div scale animation.
    try {
      const ev = new CustomEvent('stell:l1', {
        detail: { instanceId, durationMs: TIMINGS.l1.scale.duration },
      });
      document.dispatchEvent(ev);
    } catch {
      /* ignore — JSDOM may lack CustomEvent in some configs */
    }
  }, [instanceId]);

  // B — lifetime. Owns the timer and its clearTimeout. See the header: this
  // one MUST NEVER be ref-guarded.
  useEffect(() => {
    if (reducedMotion) {
      onCompleteRef.current();
      return undefined;
    }
    const timer = setTimeout(() => {
      onCompleteRef.current();
    }, TIMINGS.l1.scale.duration);
    return () => {
      clearTimeout(timer);
    };
  }, [instanceId, reducedMotion]);

  return null;
}
